<script setup>
import { onMounted, ref, computed, inject } from 'vue';
import { useStore } from 'vuex';
import useAuth from '@/composables/auth/useAuth';
import useSettings from '@/composables/app/useSettings';
import useCommonUtils from '@/composables/app/useCommonUtils';
import { name } from '@azure/msal-browser/dist/packageMetadata';
import useBulkCvBatches from '@/composables/recruitments/useBulkCvBatches';

const store = useStore();
const { authUser, authMember, hasAccess } = useAuth();
const menu_collapse = ref('dashboard');
const { teamSettings } = useSettings();
const { textCapitalize } = useCommonUtils();
const { activeBatches } = useBulkCvBatches();

const { onZiwoWindowShow } = inject('ziwo-calls');

const isAdminLoggedIn = computed(() => {
  return store.state.auth.currentRole === 'Admin';
});

const isVendorLoggedIn = computed(() => {
  return store.state.auth.currentRole === 'Vendor';
});

const canManageJobTarget = computed(() =>
  store.state.auth.currentRole === 'Admin' ||
  (store.state.auth.currentRole === 'Recruiter' && authMember.value?.super_recruiter)
);

const canOpenBulkCvParsing = computed(() => {
  const reportingRoles = ['Admin', 'Recruiter', 'Hiring Manager', 'Vendor'];
  if (!reportingRoles.includes(store.state.auth.currentRole)) return false;
  // Reporting access is separate from candidate creation: these roles can
  // inspect their server-scoped durable history even without directory access.
  if (
    ['Admin', 'Recruiter', 'Hiring Manager'].includes(
      store.state.auth.currentRole
    )
  ) {
    return true;
  }
  return (
    hasAccess({ name: 'candidate', option: 'view_own' }) ||
    hasAccess({ name: 'candidate', option: 'view_global' }) ||
    hasAccess({ name: 'candidate', option: 'create' })
  );
});
const actionRequiredBatchCount = computed(
  () =>
    activeBatches.value.filter((batch) => batch.status === 'action_required')
      .length
);

onMounted(async () => {
  const selector = document.querySelector(
    '#sidebar a[href="' + window.location.pathname + '"]'
  );
  if (selector) {
    const ul = selector.closest('ul.collapse');
    if (ul) {
      let ele = ul.closest('li.menu').querySelectorAll('.dropdown-toggle');
      if (ele) {
        ele = ele[0];
        setTimeout(() => {
          ele.click();
        });
      }
    } else {
      selector.click();
    }
  }
});

const toggleMobileMenu = () => {
  if (window.innerWidth < 991) {
    store.commit('toggleSideBar', !store.state.is_show_sidebar);
  }
};

const menuNames = computed(() => teamSettings.value?.sidebarMenuNames || {});
const resolveMenuName = (key = '') => {
  if (!key in menuNames.value) return key.split('_').join(' ');
  return menuNames.value[key]?.name
    ? menuNames.value[key]?.name
    : menuNames.value[key]?.default_name
    ? menuNames.value[key]?.default_name
    : '';
};
</script>

<template>
  <!--  BEGIN SIDEBAR  -->
  <div class="sidebar-wrapper sidebar-theme">
    <nav
      ref="menu"
      id="sidebar"
    >
      <div class="shadow-bottom"></div>

      <perfect-scrollbar
        class="list-unstyled menu-categories"
        tag="ul"
        :options="{
          wheelSpeed: 0.5,
          swipeEasing: !0,
          minScrollbarLength: 40,
          maxScrollbarLength: 300,
          suppressScrollX: true
        }"
      >
        <!-- dashboard -->
        <li class="menu">
          <router-link
            to="/"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="d-flex align-items-center">
              <FeatherIcon
                type="home"
                size="24"
                stroke-width="2"
                class="feather"
              />
              <span>{{ resolveMenuName('dashboard') }}</span>
            </div>
          </router-link>
        </li>

        <!-- recruitments -->
        <li class="menu">
          <a
            class="dropdown-toggle"
            data-bs-toggle="collapse"
            data-bs-target="#recruitments"
            aria-controls="recruitments"
            aria-expanded="false"
          >
            <div class="d-flex flex-row align-items-center">
              <FeatherIcon
                type="codesandbox"
                size="24"
                stroke-width="2"
                class="feather"
              />
              <span>{{ resolveMenuName('recruitments') }}</span>
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="feather feather-chevron-right"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </a>

          <ul
            id="recruitments"
            class="collapse submenu list-unstyled"
            data-bs-parent="#sidebar"
          >
            <li v-if="hasAccess({ name: 'org', option: 'view' })">
              <router-link
                :to="{ name: 'recruitments-organizations' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('organizations') }}
              </router-link>
            </li>
            <li v-if="hasAccess({ name: 'company', option: 'view' })">
              <router-link
                :to="{ name: 'recruitments-companies' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('companies') }}
              </router-link>
            </li>
            <li
              v-if="
                hasAccess({
                  name: 'business_unit',
                  option: 'view'
                })
              "
            >
              <router-link
                :to="{ name: 'recruitments-business-units' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('business_units') }}
              </router-link>
            </li>
            <li
              v-if="
                hasAccess({
                  name: 'department',
                  option: 'view'
                })
              "
            >
              <router-link
                :to="{ name: 'recruitments-departments' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('departments') }}
              </router-link>
            </li>
            <li
              v-if="
                hasAccess({
                  name: 'job',
                  option: 'view_own'
                }) ||
                hasAccess({
                  name: 'job',
                  option: 'view_global'
                })
              "
            >
              <router-link
                :to="{ name: 'recruitments-jobs' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('jobs') }}
              </router-link>
            </li>
            <li
              v-if="
                hasAccess({ name: 'candidate', option: 'view_own' }) ||
                hasAccess({ name: 'candidate', option: 'view_global' })
              "
            >
              <router-link
                :to="{ name: 'recruitments-candidates' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('candidates') }}
              </router-link>
            </li>
            <li v-if="hasAccess({ name: 'applicant', option: 'view' })">
              <router-link
                :to="{ name: 'recruitments-applicants' }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('applicants') }}
              </router-link>
            </li>

            <li
              v-if="
                hasAccess({
                  name: 'onboarding',
                  option: 'view'
                })
              "
            >
              <router-link
                :to="{
                  name: 'recruitments-onboarding-documents'
                }"
                @click="toggleMobileMenu"
              >
                {{ resolveMenuName('onboarding') }}
              </router-link>
            </li>
          </ul>
        </li>

        <!-- resume search -->
        <li
          class="menu"
          v-if="hasAccess({ name: 'resume_search', option: 'view' })"
        >
          <router-link
            to="/resume-search"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="d-flex align-items-center">
              <FeatherIcon
                type="search"
                size="24"
                stroke-width="2"
                class="feather"
              />
              <span>{{ resolveMenuName('resume_search') }}</span>
            </div>
          </router-link>
        </li>

        <!-- calendar -->
        <li
          class="menu"
          v-if="!isVendorLoggedIn"
        >
          <router-link
            to="/calendar"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                data-v-02c2cbc4=""
                class="feather feather-calendar"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  ry="2"
                ></rect>
                <line
                  x1="16"
                  y1="2"
                  x2="16"
                  y2="6"
                ></line>
                <line
                  x1="8"
                  y1="2"
                  x2="8"
                  y2="6"
                ></line>
                <line
                  x1="3"
                  y1="10"
                  x2="21"
                  y2="10"
                ></line>
              </svg>
              <span>{{ resolveMenuName('calendar') }}</span>
            </div>
          </router-link>
        </li>

        <!-- email -->
        <li
          class="menu"
          v-if="!isVendorLoggedIn"
        >
          <router-link
            :to="{ name: 'mailbox' }"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                data-v-02c2cbc4=""
                class="feather feather-mail"
              >
                <path
                  d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                ></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              <span>{{ resolveMenuName('email') }}</span>
            </div>
          </router-link>
        </li>

        <!-- bulk CV parsing -->
        <li
          v-if="canOpenBulkCvParsing"
          class="menu"
        >
          <router-link
            :to="{ name: 'recruitments-bulk-cv-parsing' }"
            class="dropdown-toggle bulk-cv-menu-link"
            @click="toggleMobileMenu"
          >
            <div class="d-flex align-items-center">
              <FeatherIcon
                type="file-text"
                size="20"
                stroke-width="2"
                class="feather"
              />
              <span class="bulk-cv-menu-label">Bulk CV Parsing</span>
            </div>
            <span
              v-if="activeBatches.length"
              class="bulk-cv-menu-badge ms-2 align-self-center"
              :class="{ 'needs-action': actionRequiredBatchCount }"
              :aria-label="
                actionRequiredBatchCount
                  ? `${actionRequiredBatchCount} bulk CV parsing batches need action`
                  : `${activeBatches.length} active bulk CV parsing batches`
              "
              :title="
                actionRequiredBatchCount
                  ? `${actionRequiredBatchCount} ${
                      actionRequiredBatchCount === 1
                        ? 'batch needs'
                        : 'batches need'
                    } action`
                  : `${activeBatches.length} active ${
                      activeBatches.length === 1 ? 'batch' : 'batches'
                    }`
              "
            >
              {{
                (actionRequiredBatchCount || activeBatches.length) > 99
                  ? '99+'
                  : actionRequiredBatchCount || activeBatches.length
              }}
            </span>
          </router-link>
        </li>

        <li
          v-if="canManageJobTarget && !isAdminLoggedIn"
          class="menu"
        >
          <router-link
            :to="{ name: 'job-board-master-settings-job-target-integration' }"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div>
              <FeatherIcon type="send" size="20" stroke-width="2" class="feather" />
              <span>JobTarget Settings</span>
            </div>
          </router-link>
        </li>

        <!-- master settings -->
        <li
          class="menu"
          v-if="isAdminLoggedIn"
        >
          <a
            class="dropdown-toggle"
            data-bs-toggle="collapse"
            data-bs-target="#master_settings"
            aria-controls="master_settings"
            aria-expanded="false"
          >
            <div class="">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                data-v-02c2cbc4=""
                class="feather feather-settings"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                ></circle>
                <path
                  d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
                ></path>
              </svg>
              <span>{{ resolveMenuName('master_settings') }}</span>
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="feather feather-chevron-right"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </a>

          <ul
            id="master_settings"
            class="collapse submenu list-unstyled"
            data-bs-parent="#sidebar"
          >
            <li>
              <router-link
                to="/settings/master/account-and-users"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('account_and_users') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/master-view"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('master_view') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/email"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('email') }}</router-link
              >
            </li>

            <!-- <li>
							<router-link
								:to="{ name: 'settings-team-allocation-overview' }"
								@click="toggleMobileMenu"
								>Team Allocation</router-link
							>
						</li> -->

            <!-- <li>
							<router-link
								to="/settings/master/resumes"
								@click="toggleMobileMenu"
								>Resume</router-link
							>
						</li> -->
            <li>
              <router-link
                to="/settings/master/career-page"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('career_page') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/job-board"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('job_board') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/approval-matrix"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('approval_matrix') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/onboarding"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('onboarding') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/integrations"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('integrations') }}</router-link
              >
            </li>
            <li>
              <router-link
                to="/settings/master/ex-employee"
                @click="toggleMobileMenu"
                >Ex-Employee</router-link
              >
            </li>

            <li>
              <router-link
                :to="{
                  name: 'master-settings-module-configurations'
                }"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('module_configs') }}</router-link
              >
            </li>

            <li>
              <router-link
                :to="{
                  name: 'master-settings-team-management'
                }"
                @click="toggleMobileMenu"
                >{{ resolveMenuName('team_management') }}</router-link
              >
            </li>
          </ul>
        </li>

        <!-- visa quota -->
        <li
          class="menu"
          v-if="!isVendorLoggedIn"
        >
          <router-link
            :to="{ name: 'visa-quota-companies' }"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="css-i6dzq1"
              >
                <path
                  d="M4 3h16a2 2 0 0 1 2 2v6a10 10 0 0 1-10 10A10 10 0 0 1 2 11V5a2 2 0 0 1 2-2z"
                ></path>
                <polyline points="8 10 12 14 16 10"></polyline>
              </svg>
              <span>{{ resolveMenuName('visa_quota') }}</span>
            </div>
          </router-link>
        </li>

        <!-- vendors -->
        <li
          v-if="teamSettings?.external_vendor?.enabled && isAdminLoggedIn"
          class="menu"
        >
          <router-link
            :to="{ name: 'vendors' }"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="feather feather-credit-card"
              >
                <rect
                  x="1"
                  y="4"
                  width="22"
                  height="16"
                  rx="2"
                  ry="2"
                />
                <line
                  x1="1"
                  y1="10"
                  x2="23"
                  y2="10"
                />
              </svg>
              <span>{{ resolveMenuName('vendors') }}</span>
            </div>
          </router-link>
        </li>

        <!-- file manager -->
        <li
          class="menu"
          v-if="!isVendorLoggedIn"
        >
          <router-link
            :to="{ name: 'file-manager' }"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="d-flex align-items-center">
              <FeatherIcon
                type="folder"
                size="24"
                stroke-width="2"
                class="feather"
              />
              <span>File Manager</span>
            </div>
          </router-link>
        </li>

        <!-- Reports -->
        <li
          class="menu"
          v-if="!isVendorLoggedIn"
        >
          <router-link
            :to="{ name: 'reports' }"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="">
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="css-i6dzq1"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              <span>{{ resolveMenuName('reports') }}</span>
            </div>
          </router-link>
        </li>

        <!-- feedback -->
        <li class="menu">
          <router-link
            to="/feedback"
            class="dropdown-toggle"
            @click="toggleMobileMenu"
          >
            <div class="">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                data-v-02c2cbc4=""
                class="feather feather-message-circle"
              >
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                ></path>
              </svg>
              <span>{{ resolveMenuName('feedback') }}</span>
            </div>
          </router-link>
        </li>

        <!-- feedback -->
        <li
          v-if="teamSettings?.ziwo_calls?.enabled"
          class="menu"
        >
          <div
            class="dropdown-toggle"
            @click="
              toggleMobileMenu();
              onZiwoWindowShow(true);
            "
          >
            <div class="">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="feather feather-phone-call"
              >
                <path
                  d="M15.05 5A5 5 0 0 1 19 8.95M15.05 1A9 9 0 0 1 23 8.94m-1 7.98v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                />
              </svg>
              <span>{{ `Ziwo Calls` }}</span>
            </div>
          </div>
        </li>
      </perfect-scrollbar>
    </nav>
  </div>
  <!--  END SIDEBAR  -->
</template>

<style scoped>
.feather-chevron-right {
  transition: all 0.2s ease-out;
}
</style>
